import { Timestamp as TimestampPB } from "gen/google/protobuf/timestamp_pb"
import {
  SearchDeliveriesRes_Result,
  SearchDeliveriesRes_Result_StopTransaction as StopTransactionPB,
  SearchDeliveriesRes_Result_StopTransactions as StopTransactionsPB,
} from "gen/search/service/service_pb"
import {
  DeliveryDetails as DeliveryDetailsPB,
  DeliveryStop as DeliveryStopPB,
  DeliveryStop_DeliveryAttempt as DeliveryAttemptPB,
} from "gen/txn/models_pb"

import {
  adaptDeliveryAttemptStatus,
  adaptDeliveryStatus,
  adaptDeliveryStopStatus,
  adaptSaleStatus,
  BaseDelivery,
  BaseDeliveryStop,
  DeliveryAttempt,
  DeliveryContact,
  DeliveryStopDetails,
  StopTransaction,
} from "./delivery.type"
import { FieldMapping, mapFieldsArray } from "../adapters"
import { Money } from "../classes/money"
import { attributionName } from "../classes/user"
import { UUID } from "../classes/uuid"
import { Address } from "../libs/address"
import { ensure } from "../utils"
import { deliveryWindowFromRange } from "./deliveryWindow"

export const deliveryMapping: FieldMapping<
  SearchDeliveriesRes_Result,
  BaseDelivery
> = {
  id: (res) => UUID.fromPBOrThrow(res.delivery?.id, "Missing delivery.id"),

  identifier: (res) =>
    ensure(res.delivery?.identifier, "Missing delivery.identifier"),

  status: (res) => adaptDeliveryStatus(res.status),

  departureTime: (res) =>
    TimestampPB.toDate(
      ensure(res.delivery?.departureTime, "Missing delivery.departureTime")
    ),

  isDepartureTimeEntered: (res) =>
    res.delivery?.isDepartureTimeEntered ?? false,

  returnTime: (res) => {
    const returnTime = res.delivery?.returnTime
    return returnTime ? TimestampPB.toDate(returnTime) : undefined
  },

  carrier: (res) => res.delivery?.carrier,

  driver: (res) => {
    const assignedTo = res.assignedTo
    if (!assignedTo) return undefined
    return {
      id: assignedTo.id && UUID.fromPB(assignedTo.id),
      name: `${assignedTo.firstName} ${assignedTo.lastName}`,
    }
  },

  dispatchLocation: (res) => ({
    id: UUID.fromPBOrThrow(res.location?.id, "Missing location.id"),
    prefix: ensure(res.location?.prefix, "Missing location.prefix"),
    name: ensure(res.location?.name, "Missing location.name"),
  }),

  stops: (res) => adaptStops(res.transactionsByStopId),

  notes: (res) => res.delivery?.notes,

  internalNotes: (res) => res.delivery?.internalNotes,
}

type TransactionsByStopId = { [stopId: string]: StopTransactionsPB }

const stopTransactionMapping: FieldMapping<StopTransactionPB, StopTransaction> =
  {
    id: (res) => UUID.fromPBOrThrow(res.id, "Missing transaction.id"),
    identifier: (res) =>
      ensure(res.identifier, "Missing transaction.identifier"),
    numItems: (res) => res.itemCount,
    saleStatus: (res) => adaptSaleStatus(res.status),
    total: (res) => Money.fromPB(res.total),
    customerName: (res) => res.customerName,
  }

const deliveryStopMapping: FieldMapping<StopTransactionsPB, BaseDeliveryStop> =
  {
    id: (res) => UUID.fromPBOrThrow(res.stop?.id, "Missing stop.id"),

    status: (res) => adaptDeliveryStopStatus(res.stop?.status),

    position: (res) => ensure(res.stop?.position, "Missing stop.position"),

    details: (res) => adaptDeliveryStopDetails(res.stop?.details),

    transactions: (res) =>
      mapFieldsArray(res.transactions, stopTransactionMapping),

    internalNotes: (res) => res.stop?.internalNotes ?? "",

    deliveryAttempts: (res) =>
      adaptDeliveryAttempts(res.stop?.deliveryAttemptsById),
  }

export function adaptStops(
  transactionsByStopId: TransactionsByStopId
): BaseDeliveryStop[] {
  const adaptedStops = mapFieldsArray(
    Object.values(transactionsByStopId),
    deliveryStopMapping
  )
  return adaptedStops.sort((a, b) => a.position - b.position)
}

// Accepts any proto contact, customer or delivery, and fills missing fields.
export function adaptDeliveryContact(
  contact?: Partial<DeliveryContact>
): DeliveryContact {
  return {
    name: contact?.name ?? "",
    phone: contact?.phone ?? "",
    email: contact?.email ?? "",
  }
}

export function adaptDeliveryStopDetails(
  details?: DeliveryDetailsPB
): DeliveryStopDetails {
  const contact = adaptDeliveryContact(details?.contact)
  ensure(contact.phone || contact.email, "Missing stop.contact.phone or email")

  return {
    address: Address.fromPB(ensure(details?.address, "Missing stop.address")),
    contact,
    customerContactIds: details?.customerContactIdStrings ?? [],
    notes: details?.notes ?? "",
    requestedDate: details?.requestedDate
      ? TimestampPB.toDate(details.requestedDate)
      : undefined,
    requestedWindow: deliveryWindowFromRange(details?.requestedDateRange),
  }
}

const deliveryAttemptMapping: FieldMapping<DeliveryAttemptPB, DeliveryAttempt> =
  {
    id: (attempt) => UUID.fromPBOrThrow(attempt.id, "Missing attempt.id"),
    status: (attempt) => adaptDeliveryAttemptStatus(attempt.status),
    at: (attempt) =>
      TimestampPB.toDate(ensure(attempt.at, "Missing attempt.at")),
    byName: (attempt) => attributionName(attempt.by) ?? "",
    signatureAttachmentId: (attempt) =>
      UUID.fromPBOrUndefined(attempt.signatureAttachmentId),
    photoAttachmentIds: (attempt) =>
      attempt.photoAttachmentIds.map((id) => UUID.fromPB(id)),
    notes: (attempt) => attempt.notes,
  }

export function adaptDeliveryAttempts(
  attemptsById: DeliveryStopPB["deliveryAttemptsById"] = {}
): DeliveryAttempt[] {
  return mapFieldsArray(
    Object.values(attemptsById),
    deliveryAttemptMapping
  ).sort((a, b) => b.at.getTime() - a.at.getTime())
}
